import React, { useEffect, useRef, useState } from "react";
import { Check, Clock, FlaskConical, MessageCircle, Package, Pill, Play, Scan, Syringe } from "lucide-react";
import moment from "moment";
import { formatDate } from "react-lib/utils/dateUtils";

import { AlertV4, SelectV4 } from "./ui";
import { errorTextOf } from "./errorTextV4";

import DateTextBox from "react-lib/apps/common/DateTextBox";
import SearchBoxDropdown from "react-lib/appcon/common/SearchBoxDropdown";
import { oaDivisionOptions } from "./oaProvidersV4";
import { useOAProvidersV4 } from "./useOAProvidersV4";

import { isButtonLoading } from "./sequence/sequenceStateV4";
import {
  ORDER_KIND_LABELS,
  ORDER_STATE_LABELS,
  PATIENT_LIST_V4_ACTIONS,
  PATIENT_LIST_V4_CARD,
  PATIENT_LIST_V4_SEQUENCE,
  PatientListRunParams,
  PatientListV4State,
  QUEUE_STAGES,
  QueueEncounter,
  QueueTransitionInfo,
  RawQueueEncounter,
  appointText,
  createdText,
  normalizePatientListV4Filter,
  orderGroups,
  stageOf,
  toQueueEncounter,
  waitText,
  type OrderKind,
  type OrderState,
} from "./commonV4Interface";

const ORDER_ICONS: Record<OrderKind, React.ElementType> = {
  lab: FlaskConical,
  imaging: Scan,
  treatment: Syringe,
  drug: Pill,
  supply: Package,
};

const ORDER_STATE_ICONS: Record<OrderState, React.ElementType> = {
  pending: Clock,
  progress: Play,
  complete: Check,
};

/** แสดงชื่อแพทย์ใน SearchBoxDropdown (type="Doctor") */
const doctorLabel = (doctor: any): string =>
  typeof doctor === "string"
    ? doctor
    : (doctor?.name_code ?? doctor?.name ?? doctor?.label ?? doctor?.title ?? "");

/** id ของแพทย์สำหรับ params ของ encounter-patient-optimized */
const doctorId = (doctor: any): string =>
  typeof doctor === "string" ? "" : String(doctor?.id ?? doctor?.value ?? "");

/** ตัวกรองของคิวที่ MainHISV4 เก็บไว้ (กลับจากหน้าผู้ป่วยแล้วใช้ค่าเดิม) */
export type QueueFilterState = {
  activeTab: string;
  searchHN: string;
  searchName: string;
  selectedDivision: string;
  selectedDoctor: any;
  searchDate: string;
};

export interface CardPatientListV4Props {
  apiToken?: string;
  encounterList?: (QueueEncounter | RawQueueEncounter)[];
  onSelectEncounter?: (item: any) => void;
  onActionEvent?: (action: string, item: any) => void;
  onSearch?: (filters: any) => void;
  /** เรียก sequence: props?.onEvent({ message: "RunSequence", params: {...} }) (fallback สำหรับ stand alone) */
  onEvent?: (event: any) => void;
  /** team practice: card เรียก sequence ของ MainHIS ตรง ๆ (ดู CardOrdersV4.tsx:29-31) */
  runSequence?: (params: PatientListRunParams) => void;
  /** loading ต่อ action: key "<card>_<ACTION>" */
  buttonLoadCheck?: Record<string, any>;
  /** error ของ sequenceนี้: key = ชื่อ card */
  errorMessage?: Record<string, any>;
  /** ปิดแถบ error (errorMessage.<card> = null) แบบเดียวกับ CardOrdersV4.tsx:391-393 */
  setProp?: (key: string, value: any) => void;
  /** state จาก sequence PatientListV4 */
  PatientListV4Sequence?: PatientListV4State["PatientListV4Sequence"];
  division?: number | string;
  doctorOptions?: any[];
  divisionOptions?: any[];
  /** ผลลัพท์จากการค้นหาของ SearchBoxDropdown (type="Doctor") จาก main app */
  searchedItemListWithKey?: Record<string, any>;
  savedFilter?: QueueFilterState;
  onFilterChange?: (filter: QueueFilterState) => void;
  /** แถบแท็บของเมนู แทนปุ่ม Patient Queue */
  tabs?: React.ReactNode;
  /**
   * #86727: ปุ่ม Communication Note ในคอลัมน์ HN (ทุกแถว)
   * countOf = จำนวน Note ของ encounter นั้น (แสดงต่อท้ายไอคอนเมื่อ > 0)
   * onOpen  = เปิด pop-up โดยไม่เปลี่ยนแถวที่เลือก/ไม่เปิดหน้าผู้ป่วย
   */
  communicationNote?: {
    countOf: (row: QueueEncounter) => number;
    onOpen: (row: QueueEncounter) => void;
  };
}

/** ข้อความแจ้งผลเปลี่ยนสถานะเหนือตาราง (#86606) พร้อม audit ผู้ทำ/เวลา */
const transitionMessage = (transition: NonNullable<QueueTransitionInfo>): string =>
  `คุณ ${transition.patientName || "ผู้ป่วย"} → ${transition.toLabel}`;

const auditTimeText = (at: string): string => {
  const time = moment(at);
  return time.isValid()
    ? `${time.format("HH:mm")} ${time.format("DD/MM/")}${String(time.year() + 543).slice(2)}`
    : "";
};

/** #86608: ระยะ rewalkText ของ "รอมาแล้ว" (2 นาที ตาม ticket) */
export const QUEUE_WAIT_REFRESH_MS = 2 * 60 * 1000;
/** #86608: กัน re-search ซ้ำตอนสลับแท็บรัว ๆ (tab focus/window focus ยิงถี่ยังได้ผลข้อมูลเท่าเดิม) */
export const QUEUE_AUTO_REFRESH_MIN_GAP_MS = 10 * 1000;
/**
 * #86608: poll หน้าคิว — อนุญาติ WebSocket/polling
 * ทุกระยะนี้ card จะ re-search ด้วย filter เดิม -> Encounter ใหม่ (นัด/Walk-in)
 * โผล่เป็นแถว "รอคัดกรอง" และแถวที่สถานะเปลี่ยนจากหน้าอื่น/เครื่องอื่นก็อัปเดตตาม โดยไม่ต้องกดค้นหาเอง
 */
export const QUEUE_AUTO_POLL_MS = 30 * 1000;

export const CardPatientListV4: React.FC<CardPatientListV4Props> = (props) => {
  const {
    encounterList: externalEncounterList,
    onSelectEncounter,
    onActionEvent,
    onSearch,
    PatientListV4Sequence,
    doctorOptions,
    divisionOptions,
    searchedItemListWithKey,
  } = props ?? {};
  const saved = props?.savedFilter;
  const [activeTab, setActiveTab] = useState<string>(saved?.activeTab ?? "all");
  const [searchHN, setSearchHN] = useState<string>(saved?.searchHN ?? "");
  const [searchName, setSearchName] = useState<string>(saved?.searchName ?? "");
  const [selectedDivision, setSelectedDivision] = useState<string>(
    saved?.selectedDivision ??
      String(props?.division ?? divisionOptions?.[0]?.value ?? divisionOptions?.[0]?.id ?? ""),
  );
  const [selectedDoctor, setSelectedDoctor] = useState<any>(saved?.selectedDoctor ?? null);
  const scoped = props.apiToken !== undefined;
  const allowedDivisions = oaDivisionOptions(divisionOptions);
  const validDivision = allowedDivisions.some((option) => String(option.value ?? option.id) === selectedDivision);
  const providers = useOAProvidersV4(props.apiToken, validDivision ? selectedDivision : undefined);
  const previousDivision = useRef(props.division);
  useEffect(() => {
    if (previousDivision.current !== props.division || (scoped && !validDivision)) {
      setSelectedDivision(String(props.division ?? ""));
      setSelectedDoctor(null);
      previousDivision.current = props.division;
    }
  }, [props.division, validDivision, scoped]);
  useEffect(() => {
    if (scoped && !providers.loading && selectedDoctor &&
      !providers.doctors.some((doctor) => String(doctor.id) === doctorId(selectedDoctor))) {
      setSelectedDoctor(null);
    }
  }, [scoped, providers.loading, providers.doctors, selectedDoctor]);
  const [searchDate, setSearchDate] = useState<string>(
    () => saved?.searchDate ?? formatDate(moment()),
  );
  const [filters, setFilters] = useState({
    hn: saved?.searchHN ?? "",
    name: saved?.searchName ?? "",
    doctor: doctorLabel(saved?.selectedDoctor ?? null),
  });
  /** ปิดแถบข้อความแบบ local (ข้อความเปลี่ยนสถานะชุดใหม่ key ต่างเดิม แสดงอีก) */
  const [dismissedKey, setDismissedKey] = useState<string | null>(null);
  const lastTransition = PatientListV4Sequence?.lastTransition ?? null;
  const transitionError = PatientListV4Sequence?.transitionError ?? null;

  /**
   * #86608: คอลัมน์ "รอมาแล้ว" เดินเวลาต่อเองทุก 2 นาที (re-render ล้วน ๆ ไม่เรียก API)
   * waitText คำนวณจาก moment() ณ เวลารender -> tick ใหม่ = ค่าใหม่
   */
  const [, setWaitTick] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setWaitTick((tick) => tick + 1), QUEUE_WAIT_REFRESH_MS);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    props?.onFilterChange?.({
      activeTab,
      searchHN,
      searchName,
      selectedDivision,
      selectedDoctor,
      searchDate,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, searchHN, searchName, selectedDivision, selectedDoctor, searchDate]);

  /**
   * ข้อมูลที่ใส่ตารางมาจาก sequence "PatientListV4" (encounter-patient-optimized)
   * ซึ่ง map เป็น QueueEncounter ไว้แล้วใน common/sequence/PatientListV4.ts
   * (externalEncounterList ใช้กรณีมีผู้ส่งข้อมูลมาให้เท่านั้น)
   */
  const sequenceList = PatientListV4Sequence?.encounterList;
  const hasSearched = !!PatientListV4Sequence?.sequenceIndex;
  /** loading อยู่ ที่ state กลางของ app (buttonLoadCheck) ไม่ใช่ใน slice */
  const searching = isButtonLoading(props?.buttonLoadCheck, {
    sequence: PATIENT_LIST_V4_SEQUENCE,
    card: PATIENT_LIST_V4_CARD,
    action: PATIENT_LIST_V4_ACTIONS.SEARCH,
  });
  const transitioning = isButtonLoading(props?.buttonLoadCheck, {
    sequence: PATIENT_LIST_V4_SEQUENCE,
    card: PATIENT_LIST_V4_CARD,
    action: PATIENT_LIST_V4_ACTIONS.NEXT_STAGE,
  });
  const queueError = errorTextOf(props?.errorMessage?.[PATIENT_LIST_V4_CARD]);
  const usingServerData = hasSearched || !!externalEncounterList?.length;

  const queue: QueueEncounter[] = [
    ...(hasSearched ? (sequenceList ?? []) : (externalEncounterList ?? []).map(toQueueEncounter)),
  ].sort((a, b) => String(b.created_utc).localeCompare(String(a.created_utc)));
  const doctorNames = Array.from(new Set(queue.map((item) => item.doctor_name).filter(Boolean)));
  // เมื่อค้นจาก backend ไม่ต้องกรองซ้ำด้าน client (doctor ที่เลือกเป็น id ไม่ใช่ชื่อ)
  const searched = usingServerData
    ? queue
    : queue.filter(
        (item) =>
          item.hn.includes(filters.hn.trim()) &&
          item.patient_name.toLowerCase().includes(filters.name.trim().toLowerCase()) &&
          (!filters.doctor || item.doctor_name === filters.doctor),
      );
  const displayList = searched.filter(
    (item) => activeTab === "all" || stageOf(item)?.key === activeTab,
  );

  /**
   * กดปุ่มค้นหา -> เรียก sequence "PatientListV4" ผ่าน props?.onEvent
   * (ตัว function อยู่ที่ common/sequence/PatientListV4.ts ->
   *  GET /apis/core/encounter-patient-optimized/)
   * ถ้าไม่มี onEvent (ใช้งานแบบ stand alone) ให้กรองข้อมูลใน client แทน
   */
  const handleSearch = () => {
    lastAutoRefreshAtRef.current = Date.now();
    const apiFilters = normalizePatientListV4Filter({
      filters: {
        hn: searchHN,
        name: searchName,
        date: searchDate,
        division:
          divisionOptions && divisionOptions.length > 0
            ? selectedDivision
            : (props?.division ?? ""),
        doctor: scoped && !providers.doctors.some((doctor) => String(doctor.id) === doctorId(selectedDoctor))
          ? "" : doctorId(selectedDoctor),
      },
    });

    setFilters({ hn: searchHN, name: searchName, doctor: doctorLabel(selectedDoctor) });

    const params: PatientListRunParams = {
      sequence: PATIENT_LIST_V4_SEQUENCE,
      card: PATIENT_LIST_V4_CARD,
      action: PATIENT_LIST_V4_ACTIONS.SEARCH,
      restart: !hasSearched,
      ...apiFilters,
    };

    if (props?.runSequence) {
      props.runSequence(params);
    } else {
      /* fallback: ถูก render นอก MainHIS (test / harness) */
      props?.onEvent?.({ message: "RunSequence", params: { ...params } });
    }

    onSearch?.(apiFilters);
  };

  useEffect(() => {
    if (props?.runSequence || props?.onEvent) handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * #86608: กลับเข้าแท็บ (visibility visible) / window focus -> re-search ข้อมูลล่าสุด
   * - ใช้ filter ปัจจุบันของ card เดิม (ไม่ล้างช่องค้นหา/chip: activeTab เป็น state ในตัว)
   * - จำกัดระยะห่างขั้นต่ำ QUEUE_AUTO_REFRESH_MIN_GAP_MS กัน spam ตอนสลับแท็บรัว
   * - stand alone (test/mock ไม่มี runSequence/onEvent) ไม่ผูก listener เพราะไม่มี API ให้อ่าน
   */
  const lastAutoRefreshAtRef = useRef(0);
  const handleSearchRef = useRef(handleSearch);
  const searchingRef = useRef(false);
  useEffect(() => {
    handleSearchRef.current = handleSearch;
    searchingRef.current = searching;
  });
  useEffect(() => {
    if (!props?.runSequence && !props?.onEvent) return;
    const refreshOnReturn = () => {
      if (document.visibilityState !== "visible") return;
      const now = Date.now();
      if (now - lastAutoRefreshAtRef.current < QUEUE_AUTO_REFRESH_MIN_GAP_MS) return;
      handleSearchRef.current();
    };
    const onVisibilityChange = () => refreshOnReturn();
    document.addEventListener("visibilitychange", onVisibilityChange);
    window.addEventListener("focus", refreshOnReturn);
    return () => {
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.removeEventListener("focus", refreshOnReturn);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.runSequence, props?.onEvent]);

  /**
   * #86608: เปิดหน้าคิวค้างไว้ -> poll ค้นหาใหม่ทุก QUEUE_AUTO_POLL_MS ด้วย filter/chip เดิม
   * (state ของ card ไม่ถูกล reset; ผลSearch ใหม่เขียนเข้า slice ที่ MainHIS ส่งลงมา)
   * - ข้ามตอนแท็บถูกซ่อน (กลับเข้าแท็บมี re-fetch จาก effect ข้างบนอยู่แล้ว)
   * -ข้ามตอนกำลังค้นหาอยู่ (searching จาก buttonLoadCheck)
   */
  useEffect(() => {
    if (!props?.runSequence && !props?.onEvent) return;
    const timer = setInterval(() => {
      if (document.visibilityState !== "visible" || searchingRef.current) return;
      handleSearchRef.current();
    }, QUEUE_AUTO_POLL_MS);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props?.runSequence, props?.onEvent]);

  const handleEnter = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") handleSearch();
  };

  const getTriageBadge = (triage: string) =>
    ["1", "2", "3", "4", "5"].includes(triage) ? (
      <span className={`hisv4-triage-badge triage-${triage}`}>{triage}</span>
    ) : (
      <span className="hisv4-triage-badge triage-default">-</span>
    );

  const statusPills = [
    { key: "all", label: "ทั้งหมด", count: searched.length },
    ...QUEUE_STAGES.map((stage) => ({
      key: stage.key,
      label: stage.label,
      count: searched.filter((item) => stageOf(item)?.key === stage.key).length,
    })),
  ];

  return (
    <div className="hisv4-card-patient-list">
      {/* Patient Queue Tab Button */}
      {props.tabs ?? <button className="hisv4-queue-tab-btn">Patient Queue</button>}
      {providers.error && <AlertV4 tone="danger">{providers.error} <button onClick={providers.reload}>ลองใหม่</button></AlertV4>}

      {/* Filter Row */}
      <div className="hisv4-filter-row">
        <div className="hisv4-filter-group" style={{ flex: "1 1 140px" }}>
          <label className="hisv4-filter-label">HN</label>
          <input
            type="text"
            className="hisv4-input"
            placeholder="Search..."
            value={searchHN}
            onChange={(e) => setSearchHN(e.target.value)}
            onKeyDown={handleEnter}
          />
        </div>

        <div className="hisv4-filter-group" style={{ flex: "2 1 200px" }}>
          <label className="hisv4-filter-label">ชื่อ / นามสกุล</label>
          <input
            type="text"
            className="hisv4-input"
            placeholder=""
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            onKeyDown={handleEnter}
          />
        </div>

        <div className="hisv4-filter-group" style={{ flex: "1.5 1 160px" }}>
          <label className="hisv4-filter-label">หน่วยตรวจ</label>
          <SelectV4
            aria-label="หน่วยตรวจ"
            options={(allowedDivisions.length > 0
              ? allowedDivisions.map((option: any) => ({
                  value: option?.value ?? option?.id,
                  label: option?.text ?? option?.label ?? option?.name ?? option?.title,
                }))
              : []
            ).map((option: any) => ({ value: String(option.value), text: option.label }))}
            value={String(selectedDivision ?? "")}
            onChange={(value) => { setSelectedDivision(value); setSelectedDoctor(null); }}
          />
        </div>

        <div className="hisv4-filter-group" style={{ flex: "1.5 1 160px" }}>
          <label className="hisv4-filter-label">แพทย์</label>
          {scoped ? (
            <SelectV4
              aria-label="แพทย์"
              search
              disabled={providers.loading || !!providers.error}
              options={[{ value: "", text: "ทุกผู้ให้บริการ" }, ...providers.doctors.map((doctor) => ({
                value: doctor.id, text: doctor.name_code || doctor.full_name || "",
              }))]}
              value={doctorId(selectedDoctor)}
              onChange={(value) => setSelectedDoctor(providers.doctors.find((doctor) => String(doctor.id) === value) ?? null)}
            />
          ) : props?.onEvent ? (
            <div className="hv4-search-dd hv4-search-dd-lg">
              <SearchBoxDropdown
                onEvent={props.onEvent}
                type="Doctor"
                id="QueueV4"
                placeholder="ค้นหาแพทย์"
                fluid
                useWithKey
                icon="search"
                style={{ width: "100%" }}
                defaultText={doctorLabel(selectedDoctor) || undefined}
                searchedItemListWithKey={searchedItemListWithKey}
                selectedItem={Number(doctorId(selectedDoctor)) || null}
                setSelectedItem={(_value, _key, data) => setSelectedDoctor(data)}
              />
            </div>
          ) : (
            <SelectV4
              aria-label="แพทย์"
              search
              options={[
                { value: "", text: "ทุกผู้ให้บริการ" },
                ...(doctorOptions && doctorOptions.length > 0
                  ? doctorOptions.map((option: any) => ({
                      value: option?.value ?? option?.id,
                      label: option?.label ?? option?.name ?? option?.title,
                    }))
                  : doctorNames.map((name) => ({ value: name, label: name }))
                ).map((option: any) => ({ value: String(option.value), text: option.label })),
              ]}
              value={doctorLabel(selectedDoctor)}
              onChange={setSelectedDoctor}
            />
          )}
        </div>

        <div className="hisv4-filter-group" style={{ flex: "1 1 120px" }}>
          <label className="hisv4-filter-label">วันที่</label>
          <div className="hv4-date">
            <DateTextBox
              value={searchDate}
              onChange={(date: string) => setSearchDate(date)}
              placeholder="DD/MM/YYYY (พ.ศ.)"
              useAdDate={false}
              autoAdBe={true}
              inputFluid={true}
              inputStyle={{ width: "100%" }}
            />
          </div>
        </div>

        <button className="hisv4-btn-search" onClick={handleSearch}>
          ค้นหา
        </button>
      </div>

      {/* แถบ error วางติดกับปุ่มค้นหา ผู้ใช้เห็นพร้อมปุ่มที่ตัวเองกด */}
      {!!queueError && (
        <AlertV4
          tone="danger"
          onClose={() => props?.setProp?.(`errorMessage.${PATIENT_LIST_V4_CARD}`, null)}
        >
          อ่านคิวไม่สำเร็จ: {queueError}
        </AlertV4>
      )}

      {/* ข้อความแจ้งหลังเปลี่ยนสถานะสำเร็จ (ส่งต่อ -> ขั้นถัดไป) เหนือตาราง + audit */}
      {!!lastTransition && dismissedKey !== lastTransition.at && (
        <AlertV4
          tone="info"
          className="hisv4-transition-alert"
          onClose={() => setDismissedKey(lastTransition.at)}
        >
          <span>{transitionMessage(lastTransition)}</span>
          <span className="hisv4-transition-audit">
            บันทึกโดย {lastTransition.actorName || "ไม่ระบุผู้ทำ"} เมื่อ{" "}
            {auditTimeText(lastTransition.at)}
          </span>
        </AlertV4>
      )}
      {!!transitionError && dismissedKey !== transitionError && (
        <AlertV4 tone="danger" onClose={() => setDismissedKey(transitionError)}>
          {transitionError}
        </AlertV4>
      )}

      {/* Status Pills */}
      <div className="hisv4-status-pills">
        {statusPills.map((pill) => (
          <button
            key={pill.key}
            className={`hisv4-pill ${activeTab === pill.key ? "active" : ""}`}
            onClick={() => setActiveTab(pill.key)}
          >
            <span>{pill.label}</span>
            <span className="hisv4-pill-badge">{pill.count}</span>
          </button>
        ))}
      </div>

      {/* Legend Row */}
      <div className="hisv4-legend-row">
        <span className="hisv4-legend-title">สถานะคำสั่ง:</span>
        <span className="hisv4-legend-badge pending">Pending</span>
        <span className="hisv4-legend-badge inprogress">In Progress</span>
        <span className="hisv4-legend-badge complete">Complete</span>
        <span className="hisv4-legend-note">
          - Lab/Imaging/ยา/เวชภัณฑ์/หัตถการ เปลี่ยนสถานะตามคิวของผู้ป่วย
        </span>
      </div>

      {/* Patient Table */}
      <div className="hisv4-table-card">
        <table className="hisv4-table">
          <thead>
            <tr>
              <th>HN (Encounter)</th>
              <th>ชื่อผู้รับบริการ</th>
              <th>Triage</th>
              <th>ประเภท</th>
              <th>เวลามา</th>
              <th>รอมาแล้ว</th>
              <th>เวลานัด</th>
              <th>คำสั่ง (Dr. Order)</th>
              <th>ผู้ให้บริการ</th>
              <th>สถานะ</th>
              <th>ขั้นต่อไป</th>
            </tr>
          </thead>
          <tbody>
            {displayList.length === 0 && (
              <tr>
                <td colSpan={11} className="hisv4-table-empty">
                  {searching ? "กำลังค้นหา..." : "ไม่พบผู้ป่วยในคิวตามเงื่อนไขที่เลือก"}
                </td>
              </tr>
            )}
            {displayList.map((patient, index) => {
              const stage = stageOf(patient);
              const enabled =
                !!stage && stage.key !== "done" && !(stage.key === "in-doctor" && transitioning);
              const orders = orderGroups(patient.doctor_orders, stage?.key);
              return (
                <tr
                  key={patient.id || index}
                  className={stage?.key === "wait-screen" ? "row-warm" : ""}
                  onClick={() => onSelectEncounter?.(patient)}
                >
                  <td>
                    <div className="hisv4-hn-block">
                      <span className="hisv4-hn-main">
                        {patient.hn} ({patient.number})
                      </span>
                      {patient.has_info || patient.has_warning ? (
                        <div className="hisv4-hn-icons">
                          {patient.has_info && <span className="hisv4-badge-info-round">i</span>}
                          {patient.has_warning && (
                            <span className="hisv4-badge-warning-round">!</span>
                          )}
                        </div>
                      ) : null}
                    </div>
                    {props.communicationNote ? (
                      /* #86727: ไม่มี note = กลมขอบเทาพื้นขาว (tooltip "Communication Note")
                         มี note = พื้นฟ้า #2F8FB0 ตัวขาว + จำนวนต่อท้ายไอคอน */
                      (() => {
                        const noteCount = props.communicationNote!.countOf(patient);
                        const noteTip =
                          noteCount > 0
                            ? `Communication Note (${noteCount})`
                            : "Communication Note";
                        return (
                          <button
                            type="button"
                            className={`hisv4-note-btn${noteCount > 0 ? " has-note" : ""}`}
                            title={noteTip}
                            aria-label={noteTip}
                            onClick={(e) => {
                              e.stopPropagation();
                              props.communicationNote!.onOpen(patient);
                            }}
                          >
                            <MessageCircle size={14} aria-hidden="true" />
                            {noteCount > 0 ? (
                              <span className="hisv4-note-count">
                                {noteCount > 99 ? "99+" : noteCount}
                              </span>
                            ) : null}
                          </button>
                        );
                      })()
                    ) : null}
                  </td>
                  <td>
                    {/* #86602 ข้อ 5: ช่องว่างแสดง "-" ทุกคอลัมน์ข้อมูล */}
                    <span className="hisv4-patient-name">{patient.patient_name || "-"}</span>
                  </td>
                  <td>{getTriageBadge(patient.triage_level)}</td>
                  <td>{patient.is_appointment ? "Schedule" : "Walk-in"}</td>
                  <td>{createdText(patient.created_utc)}</td>
                  <td>{stage?.key === "done" ? "-" : waitText(patient.created_utc)}</td>
                  <td>{appointText(patient.appoint_time)}</td>
                  <td>
                    {orders.length > 0 ? (
                      <div className="hisv4-order-icons-list">
                        {orders.map((order) => {
                          const Icon = ORDER_ICONS[order.kind];
                          const StateIcon = ORDER_STATE_ICONS[order.state];
                          const count = order.count > 1 ? ` ×${order.count}` : "";
                          return (
                            <span
                              key={order.kind}
                              className={`hisv4-order-pill ${order.state}`}
                              title={`${ORDER_KIND_LABELS[order.kind]}${count}: ${ORDER_STATE_LABELS[order.state]}`}
                            >
                              <Icon size={13} />
                              {count ? <span>{count.trim()}</span> : null}
                              <StateIcon size={11} />
                            </span>
                          );
                        })}
                      </div>
                    ) : (
                      "-"
                    )}
                  </td>
                  <td>{patient.doctor_name || "-"}</td>
                  <td>
                    <span className={`hisv4-status-tag ${stage?.key || "done"}`}>
                      {stage?.label || patient.status_name}
                    </span>
                  </td>
                  <td>
                    <button
                      className={`hisv4-btn-action ${enabled ? "" : "disabled"}`}
                      disabled={!enabled}
                      onClick={(e) => {
                        e.stopPropagation();
                        onActionEvent?.(stage?.action || "", patient);
                      }}
                    >
                      {stage?.action || "-"}
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="hisv4-table-count">
        {searching ? "กำลังค้นหา... " : ""}
        แสดง {displayList.length}
        {PatientListV4Sequence?.total ? ` / ${PatientListV4Sequence.total}` : ""} รายการ
      </div>
    </div>
  );
};

export default CardPatientListV4;
